import React, { useState, useEffect } from 'react';
import api from '../../../../api';
import styles from './SendPushNotificationFilter.module.css';
import Icon from '../../../../campaigns/components/Icon';
import DropdownSelector from '../../DropdownSelector/DropdownSelector';
import DebounceInput from '../../../../campaigns/components/DebounceInput/DebounceInput';

const SendPushNotificationFilter = props => {
  const {
    filterConfiguration,
    FILTER_CONSTANTS,
    activeFilters,
    setActiveFilters
  } = props;

  const [isCountFetching, setIsCountFetching] = useState(false);
  const [targetUsers, setTargetUsers] = useState(false);

  useEffect(
    () => {
      const getPushNotificationTargetCount = async activeFiltersWithValue => {
        setIsCountFetching(true);
        const pushNotificationTargetCount = await api().getPushNotificationTargetCount(
          activeFiltersWithValue
        );
        setTargetUsers(pushNotificationTargetCount.json.count);
        setIsCountFetching(false);
      };

      const activeFiltersWithValue = activeFilters.filter(filter => {
        if (filter.name === '') return false;
        if (Array.isArray(filter.values)) {
          return !!filter.values.length;
        }
        if (Object.keys(filter.values).length) return true;
        return null;
      });

      getPushNotificationTargetCount(activeFiltersWithValue);
    },
    [activeFilters]
  );

  const onAddFilter = () => {
    const newFilter = {
      name: ''
    };

    setActiveFilters([...activeFilters, ...[newFilter]]);
  };

  const filterTypesOptions = () => {
    const availableOptions = [];
    Object.keys(filterConfiguration).forEach(availableFilter => {
      const checkIsFilterSelected = activeFilters.filter(
        activeFilter => activeFilter.name === availableFilter
      );
      if (!checkIsFilterSelected || checkIsFilterSelected.length === 0) {
        availableOptions.push({
          value: availableFilter,
          label: filterConfiguration[availableFilter].label
        });
      }
      return null;
    });
    return availableOptions;
  };

  const updateActiveFilters = (name, values) => {
    let activeFiltersToUpdate;

    activeFilters.forEach(activeFilter => {
      if (activeFilter.name === name) {
        activeFiltersToUpdate = activeFilter;
      }
    });

    if (activeFiltersToUpdate) {
      activeFiltersToUpdate.values = values;
      setActiveFilters([...activeFilters]);
    }
  };

  const showFilterValues = filter => {
    if (
      filterConfiguration[filter.name] &&
      filterConfiguration[filter.name].type === FILTER_CONSTANTS.MULTIPLE_CHOICE
    ) {
      return multipleChoiceField(filterConfiguration[filter.name]);
    }
    if (
      filterConfiguration[filter.name] &&
      filterConfiguration[filter.name].type === FILTER_CONSTANTS.RANGE
    ) {
      return rangeField(filterConfiguration[filter.name]);
    }
    if (
      filterConfiguration[filter.name] &&
      filterConfiguration[filter.name].type === FILTER_CONSTANTS.SINGLE_OPTION
    ) {
      return singleChoiceField(filterConfiguration[filter.name]);
    }
    return null;
  };

  const singleChoiceField = filter => {
    const placeholder = FILTER_CONSTANTS.DEFAULT_PLACEHOLDER;
    const singleActiveFilterListValue = activeFilters.filter(
      activeFilter => activeFilter.name === filter.name
    );
    const singleActiveFilterValue =
      singleActiveFilterListValue && singleActiveFilterListValue.length
        ? singleActiveFilterListValue[0].values
        : null;

    return (
      <DropdownSelector
        options={filter.options}
        fetchOptions={filter.fetchOptions}
        value={singleActiveFilterValue}
        onSelectCompleted={(value, label) => {
          updateActiveFilters(filter.name, {
            ...singleActiveFilterListValue[0].values,
            ...{ value, label }
          });
        }}
        placeholder={placeholder}
        search={filter.search}
      />
    );
  };

  const multipleChoiceField = filter => {
    const placeholder = FILTER_CONSTANTS.DEFAULT_PLACEHOLDER;
    const singleActiveFilterListValue = activeFilters.filter(
      activeFilter => activeFilter.name === filter.name
    );
    const singleActiveFilterValue =
      singleActiveFilterListValue && singleActiveFilterListValue.length
        ? singleActiveFilterListValue[0].values
        : null;
    const arrayActiveFilterString = singleActiveFilterValue.map(
      arrayFilter => arrayFilter.label
    );
    const multipleChoiceFieldValue = {
      value: singleActiveFilterValue,
      label: arrayActiveFilterString.join()
    };

    return (
      <DropdownSelector
        options={filter.options}
        fetchOptions={filter.fetchOptions}
        value={multipleChoiceFieldValue}
        onSelectCompleted={(value, label) => {
          const existingMultipleValue = singleActiveFilterListValue[0].values.filter(
            filterList => filterList.value === value
          );

          if (existingMultipleValue && existingMultipleValue.length) {
            const excludedValues = singleActiveFilterListValue[0].values.filter(
              activeFilter => activeFilter.value !== value
            );
            updateActiveFilters(filter.name, excludedValues);
          } else {
            updateActiveFilters(filter.name, [
              ...singleActiveFilterListValue[0].values,
              ...[{ value, label }]
            ]);
          }
        }}
        placeholder={placeholder}
        search={filter.search}
        multiple
      />
    );
  };

  const rangeField = filter => (
    <div className={styles.rangeFieldContainer}>
      <div className={styles.rangeFieldContainerStartContainer}>
        <DebounceInput
          placeholder={filter && filter.gteLabel}
          initialValue={
            filter.name &&
            activeFilters[filter.name] &&
            activeFilters[filter.name].values &&
            activeFilters[filter.name].values.gte
          }
          onChangeValue={searchValue => {
            const singleRangeValue = activeFilters.filter(
              singleRangeFilter => singleRangeFilter.name === filter.name
            );
            const existingRangeValue =
              singleRangeValue && singleRangeValue[0]
                ? singleRangeValue[0].values
                : {};
            if (searchValue && searchValue !== '') {
              updateActiveFilters(filter.name, {
                ...existingRangeValue,
                ...{ gte: searchValue }
              });
            } else {
              delete existingRangeValue.gte;
              updateActiveFilters(filter.name, {
                ...existingRangeValue
              });
            }
          }}
          className={styles.rangeFilterInput}
        />
      </div>
      <div className={styles.rangeFieldContainerEndContainer}>
        <DebounceInput
          placeholder={filter && filter.lteLabel}
          initialValue={
            filter.name &&
            activeFilters[filter.name] &&
            activeFilters[filter.name].values &&
            activeFilters[filter.name].values.lte
          }
          onChangeValue={searchValue => {
            const singleRangeValue = activeFilters.filter(
              singleRangeFilter => singleRangeFilter.name === filter.name
            );
            const existingRangeValue =
              singleRangeValue && singleRangeValue[0]
                ? singleRangeValue[0].values
                : {};
            if (searchValue && searchValue !== '') {
              updateActiveFilters(filter.name, {
                ...existingRangeValue,
                ...{ lte: searchValue }
              });
            } else {
              delete existingRangeValue.lte;
              updateActiveFilters(filter.name, {
                ...existingRangeValue
              });
            }
          }}
          className={styles.rangeFilterInput}
        />
      </div>
    </div>
  );

  const renderFilter = (filter, index) => {
    if (!filter.type) {
      return (
        <div key={`push-notification-filter-${index}`}>
          <div className={styles.filterItemContainer}>
            <div className={styles.filterType}>
              <span
                className={styles.closeIcon}
                role="presentation"
                onClick={() => {
                  activeFilters.splice(index, 1);
                  setActiveFilters([...activeFilters]);
                }}
              >
                <Icon type="close" />
              </span>
              <DropdownSelector
                options={filterTypesOptions()}
                value={
                  filter && filter.label
                    ? { value: filter.name, label: filter.label }
                    : {}
                }
                onSelectCompleted={(value, label) => {
                  const newFilter = filter;
                  const newFilterConfiguration = filterConfiguration[value];
                  newFilter.name = value;
                  newFilter.label = label;

                  newFilter.values =
                    newFilterConfiguration.type ===
                    FILTER_CONSTANTS.MULTIPLE_CHOICE
                      ? []
                      : {};
                  setActiveFilters([...activeFilters]);
                }}
                placeholder={FILTER_CONSTANTS.DEFAULT_PLACEHOLDER}
              />
            </div>
            <div className={styles.filterType}>{showFilterValues(filter)}</div>
          </div>
          {index < activeFilters.length - 1 ? (
            <div className={styles.andContainer}>
              <span className={styles.and}>AND</span>
            </div>
          ) : null}
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <div className={styles.filterTopContainer}>
        <div className={styles.counter}>
          Target group:{' '}
          <strong>{isCountFetching ? '-' : targetUsers} devices</strong>
        </div>
        <div
          className={styles.addFilterButton}
          role="presentation"
          onClick={() => onAddFilter()}
        >
          Add filter
          <span className={styles.addFilterButtonIcon}>
            <Icon type="add" />
          </span>
        </div>
      </div>
      <div>
        {activeFilters.map((filter, index) => renderFilter(filter, index))}
      </div>
    </div>
  );
};

export default SendPushNotificationFilter;
